import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from "@/components/ui/popover";
import { ConditionIcon } from "@/features/weather/components/condition-icon.tsx";
import {
	formatTemp,
	HourlyStrip,
} from "@/features/weather/components/hourly-strip.tsx";
import { WeatherAlert } from "@/features/weather/components/weather-alert.tsx";
import { WorkableBadge } from "@/features/weather/components/workable-badge.tsx";
import { useWeather } from "@/features/weather/hooks/use-weather.ts";

function Metric({
	label,
	value,
	hint,
}: {
	label: string;
	value: string;
	hint?: string;
}) {
	return (
		<div className="weather-metric">
			<p className="weather-metric__label">{label}</p>
			<p className="weather-metric__value">{value}</p>
			{hint && <p className="weather-metric__hint">{hint}</p>}
		</div>
	);
}

function formatPercent(value: number | null): string {
	return value === null ? "—" : `${value}%`;
}

export function WeatherButton() {
	const { query, hasContext, obraName, locationLabel } = useWeather();

	if (!hasContext || query.isError) {
		return null;
	}

	const weather = query.data;
	if (!weather) {
		return (
			<Button
				variant="outline"
				className="weather-button"
				disabled
				aria-label="Carregando previsão do tempo"
			>
				<Loader2 className="weather-button__spinner" />
			</Button>
		);
	}

	return (
		<Popover>
			<PopoverTrigger asChild>
				<Button
					variant="outline"
					className="weather-button"
					aria-label={`Previsão do tempo: ${formatTemp(weather.temp)}, ${obraName}`}
				>
					<ConditionIcon
						condition={weather.condition}
						className="weather-button__icon"
					/>
					<span className="weather-button__temp">
						{formatTemp(weather.temp)}
					</span>
					{obraName && (
						<span className="weather-button__district">· {obraName}</span>
					)}
				</Button>
			</PopoverTrigger>

			<PopoverContent align="end" className="weather-panel">
				<p className="weather-panel__location">{locationLabel}</p>
				<div className="weather-panel__current">
					<div className="weather-panel__summary">
						<div className="weather-panel__current-temp">
							<ConditionIcon
								condition={weather.condition}
								className="weather-panel__current-icon"
							/>
							<span className="weather-panel__temp">
								{formatTemp(weather.temp)}
							</span>
						</div>
						<p className="weather-panel__description">{weather.description}</p>
					</div>
					<WorkableBadge
						workable={weather.workable}
						className="weather-panel__badge"
					/>
				</div>

				<div className="weather-panel__metrics">
					<Metric label="Umidade" value={formatPercent(weather.humidity)} />
					<Metric
						label="Chuva"
						value={formatPercent(weather.precipProbability)}
					/>
					<Metric
						label="UV"
						value={weather.uvIndex === null ? "—" : String(weather.uvIndex)}
						hint={weather.uvLabel}
					/>
				</div>

				{weather.hourly.length > 0 && (
					<>
						<p className="weather-panel__section-title">Próximas horas</p>
						<HourlyStrip hours={weather.hourly} />
					</>
				)}

				{weather.alert && (
					<WeatherAlert
						message={weather.alert}
						className="weather-panel__alert"
					/>
				)}
			</PopoverContent>
		</Popover>
	);
}
